/**
 * eformsAdmin.service.ts — Admin > Forms & Templates > E-Forms: the account's
 * e-forms (the legacy `surveys` tables) through the API v5 calls the corporate
 * dashboard's E-Forms page and Form Builder use:
 *   - /surveys: list (every form, deleted ones too), create, update, delete
 *     (soft: active 0), restore; GET /surveys/{id} for one live form with its
 *     questions (404 for a deleted one) and the direct / embed links;
 *   - PUT /surveys/{id}/fields: every question at once, in order — new ones
 *     created, sent ones updated, the rest DELETED (with their logic);
 *   - GET /surveys/{id}/logic-rules, GET /surveys/options (each option group's
 *     options), /survey-question-types (option groups: list, create, update —
 *     403 SHARED_GROUP when another account uses the group);
 *   - lookups: /lead-sources, /patient-demographics, /payments/merchants.
 *
 * Fixed here against corp (its E list): a new form gets the next list order
 * (corp sent 1 for all); logic on a new question is saved; "No Group" clears a
 * question's group; Populates saves the demographic key the e-form processor
 * reads (corp re-sent the old numeric id, which wipes it); edited options are
 * saved to their group with Save All, renamed or removed ones too, and a
 * shared group stops the save with the reason (corp dropped both); emoji in
 * any text is stored as an HTML entity (the columns are utf8mb3).
 * Writes need Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

// ─── Helpers ───────────────────────────────────────────────────────────────────

function rows(data: unknown): Record<string, unknown>[] {
  return Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
}

const num = (v: unknown) => Number(v ?? 0) || 0;
const str = (v: unknown) => (v == null ? '' : String(v));
const on = (v: unknown) => v === true || v === 1 || v === '1';

/** Characters outside the BMP (emoji) as HTML entities: the survey columns are utf8mb3 (MySQL 3988). */
export function encodeEmoji(text: string): string {
  return text.replace(/[\u{10000}-\u{10FFFF}]/gu, (ch) => `&#x${ch.codePointAt(0)!.toString(16).toUpperCase()};`);
}

/** HTML to plain text (tags dropped, entities decoded). */
export function plainText(html: string): string {
  if (!html) return '';
  if (typeof DOMParser !== 'undefined') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return (doc.body.textContent ?? '').replace(/\s+/g, ' ').trim();
  }
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

/** True when HTML holds text or media (corp's content rule). */
export function htmlHasContent(html: string): boolean {
  return plainText(html) !== '' || /<(img|video|iframe|object|embed|svg|canvas)\b/i.test(html);
}

// ─── Forms ─────────────────────────────────────────────────────────────────────

/** surveys.surveyType: corp's list (the API's form-types call answers input types). */
export const FORM_TYPES = [
  { value: 1, label: 'Multi-use' },
  { value: 2, label: 'Lead Form' },
  { value: 3, label: 'Patient Note Template' },
  { value: 4, label: 'Patient Survey' },
];

export interface EForm {
  id: number;
  title: string;
  active: boolean;
  surveyType: number | null;
  contactMe: boolean;
  headerMessage: string;
  submissionMessage: string;
  pageTitle: string;
  expiresIn: number;
  shortcode: string;
  color1: string;
  color2: string;
  fontColor: string;
  order: number;
  displayLogo: boolean;
  leadSourceId: number;
  submissionUrl: string;
  emailContentId: number;
  smsId: number;
  autoresponderId: number;
  directLink: string;
  legacyDirectLink: string;
  embedCode: string;
  questionCount: number;
}

export function toEForm(r: Record<string, unknown>): EForm {
  return {
    id: num(r.id ?? r.surveysID),
    title: str(r.title ?? r.surveyTitle),
    active: r.isActive !== undefined ? on(r.isActive) : on(r.active),
    surveyType: r.surveyType == null ? null : num(r.surveyType),
    contactMe: on(r.contactMe),
    headerMessage: str(r.headerMessage),
    submissionMessage: str(r.submissionMessage),
    pageTitle: str(r.pageTitle),
    expiresIn: num(r.expiresInDays ?? r.expiresIn),
    shortcode: str(r.shortcode),
    color1: str(r.color1),
    color2: str(r.color2),
    fontColor: str(r.fontColor),
    order: num(r.surveyOrder),
    displayLogo: on(r.displayLogo),
    leadSourceId: num(r.leadSourceId ?? r.leadSourceID),
    submissionUrl: str(r.submissionUrl),
    emailContentId: num(r.emailContentId ?? r.emailContentID),
    smsId: num(r.smsId ?? r.smsID),
    autoresponderId: num(r.autoresponderID),
    directLink: str(r.directLink),
    legacyDirectLink: str(r.legacyDirectLink),
    embedCode: str(r.embedCode),
    questionCount: num(r.questionCount),
  };
}

/** Every form of the account, deleted ones too. */
export async function listEForms(): Promise<EForm[]> {
  const out: EForm[] = [];
  for (let page = 1; ; page++) {
    const r = await apiClient.get<{ data?: unknown; meta?: { totalPages?: number } }>('/surveys', { params: { page, perPage: 5000 } });
    out.push(...rows(r.data?.data).map(toEForm));
    if (page >= (num(r.data?.meta?.totalPages) || 1)) return out;
  }
}

/** The settings dialogs' fields. */
export interface EFormSettings {
  title: string;
  pageTitle: string;
  surveyType: number;
  headerMessage: string;
  submissionMessage: string;
  shortcode: string;
  expiresIn: number;
  submissionUrl: string;
  color1: string;
  color2: string;
  fontColor: string;
  active: boolean;
  contactMe: boolean;
  displayLogo: boolean;
  /** A level-2 lead source (legacy); 0 = none. */
  leadSourceId: number;
  emailContentId: number;
  smsId: number;
}

export function newEFormSettings(): EFormSettings {
  return {
    title: '',
    pageTitle: '',
    surveyType: 1,
    headerMessage: '',
    submissionMessage: '',
    shortcode: '',
    expiresIn: 0,
    submissionUrl: '',
    color1: '#FFFFFF',
    color2: '#000000',
    fontColor: '#333333',
    active: true,
    contactMe: false,
    displayLogo: false,
    leadSourceId: 0,
    emailContentId: 0,
    smsId: 0,
  };
}

export function settingsFrom(f: EForm): EFormSettings {
  return {
    title: f.title,
    pageTitle: f.pageTitle,
    surveyType: f.surveyType ?? 0,
    headerMessage: f.headerMessage,
    submissionMessage: f.submissionMessage,
    shortcode: f.shortcode,
    expiresIn: f.expiresIn,
    submissionUrl: f.submissionUrl,
    color1: f.color1,
    color2: f.color2,
    fontColor: f.fontColor,
    active: f.active,
    contactMe: f.contactMe,
    displayLogo: f.displayLogo,
    leadSourceId: f.leadSourceId,
    emailContentId: f.emailContentId,
    smsId: f.smsId,
  };
}

/**
 * The write body. Create also sends Active, Contact Me and Expires In (corp
 * offers them only there) and no list order (the API appends the form).
 * Update never sends the autoresponder: there is nothing to pick it from yet,
 * and the stored one is kept.
 */
export function eformPayload(s: EFormSettings, isNew: boolean): Record<string, unknown> {
  const body: Record<string, unknown> = {
    surveyTitle: encodeEmoji(s.title.trim()),
    pageTitle: encodeEmoji(s.pageTitle.trim()),
    surveyType: s.surveyType || 0,
    headerMessage: encodeEmoji(s.headerMessage),
    submissionMessage: encodeEmoji(s.submissionMessage),
    shortcode: s.shortcode.trim(),
    submissionUrl: s.submissionUrl.trim(),
    color1: s.color1,
    color2: s.color2,
    fontColor: s.fontColor,
    displayLogo: s.displayLogo,
    leadSourceID: s.leadSourceId || 0,
    emailContentID: s.emailContentId || 0,
    smsID: s.smsId || 0,
  };
  if (isNew) {
    body.active = s.active;
    body.contactMe = s.contactMe;
    body.expiresIn = Math.max(0, Math.floor(s.expiresIn || 0));
  }
  return body;
}

export function createEForm(s: EFormSettings): Promise<number> {
  return apiClient
    .post<ApiResponse<Record<string, unknown>>>('/surveys', eformPayload(s, true))
    .then((r) => num(r.data?.data?.surveysID ?? r.data?.data?.id));
}

export function updateEForm(id: number, s: EFormSettings): Promise<void> {
  return apiClient.put(`/surveys/${id}`, eformPayload(s, false)).then(() => undefined);
}

export function deleteEForm(id: number): Promise<void> {
  return apiClient.delete(`/surveys/${id}`).then(() => undefined);
}

export function restoreEForm(id: number): Promise<void> {
  return apiClient.post(`/surveys/${id}/restore`).then(() => undefined);
}

// ─── Lookups ───────────────────────────────────────────────────────────────────

export interface LeadSourceNode {
  id: number;
  name: string;
  active: boolean;
  children: { id: number; name: string; active: boolean }[];
}

/** Lead source categories (level 1) with their sources (level 2). */
export function listLeadSourceTree(): Promise<LeadSourceNode[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/lead-sources').then((r) =>
    rows(r.data?.data).map((c) => ({
      id: num(c.referralLevel1ID),
      name: str(c.name),
      active: on(c.active) || c.active === undefined,
      children: rows(c.leadSources).map((s) => ({ id: num(s.referralLevel2ID), name: str(s.name), active: on(s.active) || s.active === undefined })),
    })),
  );
}

export interface DemographicField {
  id: number;
  /** patients_demographics.question, e.g. first_name — what "Populates" stores. */
  key: string;
  label: string;
}

/** The record fields an answer can fill in ("Populates"), live ones in their order. */
export function listDemographicFields(): Promise<DemographicField[]> {
  return apiClient.get<ApiResponse<Record<string, unknown>[]>>('/patient-demographics').then((r) =>
    rows(r.data?.data)
      .filter((d) => d.active === undefined || on(d.active))
      .sort((a, b) => num(a.orderID) - num(b.orderID))
      .map((d) => {
        const key = str(d.question);
        return {
          id: num(d.patientsDemographicsID),
          key,
          label: key
            .split('_')
            .filter(Boolean)
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
            .join(' '),
        };
      }),
  );
}

/** The current direct / legacy / embed links of a live form. */
export function getEFormLinks(id: number): Promise<Pick<EForm, 'directLink' | 'legacyDirectLink' | 'embedCode'>> {
  return apiClient.get<ApiResponse<Record<string, unknown>>>(`/surveys/${id}`).then((r) => {
    const d = r.data?.data ?? {};
    return { directLink: str(d.directLink), legacyDirectLink: str(d.legacyDirectLink), embedCode: str(d.embedCode) };
  });
}

// ─── Questions ─────────────────────────────────────────────────────────────────

export type QuestionGroupName = 'Basic Fields' | 'Choice Fields' | 'Special Fields' | 'System Fields';

export interface QuestionTypeDef {
  id: number;
  label: string;
  group: QuestionGroupName;
  description: string;
}

/** Corp's palette (the question type ids are the legacy ones). */
export const QUESTION_TYPES: QuestionTypeDef[] = [
  { id: 10, label: 'Short Text', group: 'Basic Fields', description: 'Single-line text input' },
  { id: 9, label: 'Long Text', group: 'Basic Fields', description: 'Multi-line textarea' },
  { id: 18, label: 'Name', group: 'Basic Fields', description: 'First and last name fields' },
  { id: 14, label: 'Date', group: 'Basic Fields', description: 'Date picker' },
  { id: 22, label: 'Address', group: 'Basic Fields', description: 'Full address fields' },
  { id: 20, label: 'Submit Button', group: 'Basic Fields', description: 'Form submit button' },
  { id: 7, label: 'Radio Buttons', group: 'Choice Fields', description: 'Single-select options' },
  { id: 8, label: 'Checkboxes', group: 'Choice Fields', description: 'Multi-select options' },
  { id: 16, label: 'Dropdown', group: 'Choice Fields', description: 'Single-select dropdown' },
  { id: 12, label: 'File Upload', group: 'Special Fields', description: 'File attachment' },
  { id: 11, label: 'Signature', group: 'Special Fields', description: 'Signature pad' },
  { id: 19, label: 'Content Block', group: 'Special Fields', description: 'Static HTML content (no scroll)' },
  { id: 23, label: 'Content Scroll', group: 'Special Fields', description: 'Static HTML content (scrollable)' },
  { id: 28, label: 'Scroll & Accept', group: 'Special Fields', description: 'Scrollable content with acceptance checkbox' },
  { id: 13, label: 'Page Break', group: 'Special Fields', description: 'Multi-page divider' },
  { id: 21, label: 'Credit Card Entry', group: 'Special Fields', description: 'Payment card fields' },
  { id: 17, label: 'BMI Calculator', group: 'Special Fields', description: 'Height and weight with BMI' },
  { id: 27, label: 'Email/SMS Opt-In', group: 'Special Fields', description: 'Email and SMS opt-in toggles' },
  { id: 15, label: 'Hidden Field', group: 'System Fields', description: 'Hidden from user' },
  { id: 24, label: 'Timestamp', group: 'System Fields', description: 'Read-only date stamp' },
  { id: 26, label: 'Client IP', group: 'System Fields', description: 'Capture IP address' },
  { id: 25, label: 'Patient Note', group: 'System Fields', description: 'Patient note field' },
];

export const QUESTION_GROUPS: QuestionGroupName[] = ['Basic Fields', 'Choice Fields', 'Special Fields', 'System Fields'];

export function questionTypeLabel(type: number): string {
  return QUESTION_TYPES.find((t) => t.id === type)?.label ?? 'Unknown';
}

export const CHOICE_TYPES = [7, 8, 16];
export const PAGE_BREAK = 13;
export const ADDRESS = 22;
export const CREDIT_CARD = 21;
/** Display-only types: nothing to answer, so no Required switch. */
export const DISPLAY_ONLY_TYPES = [19, 23, 20, 24, 26];
/** Types whose body is "Content" rather than "Question Text". */
export const CONTENT_TYPES = [19, 23, 28];
/** Types a logic rule can't test. */
export const NOT_LOGIC_TARGETS = [11, 12, 13, 19, 20];

/** Address's required parts (a bitmask kept in customQuestionType). */
export const ADDRESS_PARTS = [
  { bit: 1, label: 'Street Address' },
  { bit: 2, label: 'City' },
  { bit: 4, label: 'State' },
  { bit: 8, label: 'Zip Code' },
];

export interface QuestionOption {
  /** surveys_options id (a group's) or surveys_options_custom id (a legacy set's); null = new. */
  id: number | null;
  text: string;
}

export interface LogicRule {
  /** null = new. */
  id: number | null;
  /** The question it tests (its surveysOrderID). */
  targetOrderId: number | null;
  /** 0 is, 1 is not, 2 <, 3 >. */
  operator: number;
  /** The answer it compares with (an option), 0 = none. */
  optionId: number;
  /** Or the typed value. */
  value: string;
}

export interface BuilderField {
  /** Stable while editing: `o<surveysOrderID>` or `n<counter>`. */
  key: string;
  /** surveysOrderID; null until saved. */
  orderId: number | null;
  type: number;
  /** HTML (a page break's title is plain text). */
  content: string;
  /** HTML; a Credit Card's merchant id. */
  instruction: string;
  required: boolean;
  addOther: boolean;
  checkAll: boolean;
  /** 0 everyone, 1 patients, 2 non-patients. */
  showTo: number;
  summary: boolean;
  /** The option group (customQuestionType) — choice types. */
  optionGroupId: number;
  /** Address's required parts (customQuestionType) — Address only. */
  addressMask: number;
  /** A legacy per-question option set (surveysQuestionTypeCustomID), kept as is. */
  customSetId: number;
  /** The legacy set's options, read-only reference. */
  customSetOptions: QuestionOption[];
  /** Edited options (null = the group's / the set's as they are). */
  options: QuestionOption[] | null;
  /** Question group (section) id; 0 = none. */
  groupId: number;
  /** A demographic key (or a legacy numeric id); '' = none. */
  populates: string;
  /** 0 do nothing, 1 show, 2 hide. */
  logicAction: number;
  /** 0 all, 1 any. */
  logicCondition: number;
  rules: LogicRule[];
}

export interface OptionGroup {
  id: number;
  title: string;
  options: QuestionOption[];
}

export interface QuestionGroup {
  id: number;
  name: string;
}

export interface BuilderData {
  fields: BuilderField[];
  groups: QuestionGroup[];
  optionGroups: OptionGroup[];
}

let newKey = 0;
export function nextFieldKey(): string {
  newKey += 1;
  return `n${newKey}`;
}

export function newBuilderField(type: number): BuilderField {
  return {
    key: nextFieldKey(),
    orderId: null,
    type,
    // As corp: a new question reads as its type until written (a page break has no title).
    content: type === PAGE_BREAK ? '' : questionTypeLabel(type),
    instruction: '',
    required: false,
    addOther: false,
    checkAll: false,
    showTo: 0,
    summary: false,
    optionGroupId: 0,
    addressMask: 0,
    customSetId: 0,
    customSetOptions: [],
    options: null,
    groupId: 0,
    populates: '',
    logicAction: 0,
    logicCondition: 0,
    rules: [],
  };
}

function pick(o: Record<string, unknown> | null | undefined, ...keys: string[]): unknown {
  if (!o) return undefined;
  for (const k of keys) if (o[k] !== undefined) return o[k];
  return undefined;
}

function toOptionGroups(groupsList: unknown, optionsByGroup: unknown): OptionGroup[] {
  const byGroup = (optionsByGroup ?? {}) as Record<string, unknown>;
  return rows(groupsList).map((g) => {
    const id = num(g.id ?? g.surveysQuestionTypeID);
    return {
      id,
      title: str(g.title),
      options: rows(byGroup[String(id)])
        .sort((a, b) => num(a.sOrder) - num(b.sOrder))
        .map((o) => ({ id: num(o.surveysOptionID) || null, text: str(o.optionText) })),
    };
  });
}

/** The account's option groups with their options (new options get their ids here). */
export async function listOptionGroups(): Promise<OptionGroup[]> {
  const [options, groupsList] = await Promise.all([
    apiClient.get<ApiResponse<{ optionsByQuestionType?: Record<string, unknown> }>>('/surveys/options'),
    apiClient.get<ApiResponse<Record<string, unknown>[]>>('/survey-question-types'),
  ]);
  return toOptionGroups(groupsList.data?.data, options.data?.data?.optionsByQuestionType);
}

/** A form's questions, groups and the account's option groups, ready to edit. */
export async function loadBuilder(surveyId: number, demographics: DemographicField[] = []): Promise<BuilderData> {
  const [show, logic, options, groupsList] = await Promise.all([
    apiClient.get<ApiResponse<Record<string, unknown>>>(`/surveys/${surveyId}`),
    apiClient.get<ApiResponse<{ logicRules?: unknown }>>(`/surveys/${surveyId}/logic-rules`).catch(() => null),
    apiClient.get<ApiResponse<{ optionsByQuestionType?: Record<string, unknown> }>>('/surveys/options').catch(() => null),
    apiClient.get<ApiResponse<Record<string, unknown>[]>>('/survey-question-types').catch(() => null),
  ]);
  const survey = show.data?.data ?? {};

  const optionGroups = toOptionGroups(groupsList?.data?.data, options?.data?.data?.optionsByQuestionType);

  const groups: QuestionGroup[] = rows(survey.groups).map((g) => ({
    id: num(g.surveysOrderGroupID ?? g.id),
    name: str(g.groupTitle ?? g.name),
  }));

  // Logic: one record per question (action + condition) and its rules.
  const logicByOrder = new Map<number, { action: number; condition: number; rules: LogicRule[] }>();
  for (const r of rows(logic?.data?.data?.logicRules)) {
    const orderId = num(r.surveysOrderID);
    const entry = logicByOrder.get(orderId) ?? { action: num(r.logicAction), condition: num(r.logicCondition), rules: [] };
    entry.rules.push({
      id: num(r.surveysItemLogicRuleID) || null,
      targetOrderId: num(r.targetItemID) || null,
      operator: num(r.logicBoolean),
      optionId: num(r.surveysOptionID),
      value: str(r.responseContent),
    });
    logicByOrder.set(orderId, entry);
  }

  const demographicById = new Map(demographics.map((d) => [d.id, d.key]));

  const fields = rows(survey.orders)
    .sort((a, b) => num(a.iOrder) - num(b.iOrder))
    .map((o): BuilderField => {
      const item = (o.item ?? {}) as Record<string, unknown>;
      const groupItem = (pick(o, 'group_item', 'groupItem') ?? null) as Record<string, unknown> | null;
      const orderId = num(o.surveysOrderID);
      const type = num(item.questionType);
      const custom = num(item.customQuestionType);
      const recordId = num(item.recordFieldID);
      const recordText = str(item.recordFieldText).trim();
      const logicEntry = logicByOrder.get(orderId);
      return {
        key: `o${orderId}`,
        orderId,
        type,
        content: str(item.questionContent),
        instruction: str(item.instructionText),
        required: on(o.isRequired),
        addOther: on(o.addOther),
        checkAll: on(o.checkAll),
        showTo: num(o.showTo),
        summary: on(item.isSummary),
        optionGroupId: type === ADDRESS ? 0 : custom,
        addressMask: type === ADDRESS ? custom : 0,
        customSetId: num(item.surveysQuestionTypeCustomID),
        customSetOptions: rows(pick(item, 'custom_options', 'customOptions'))
          .sort((a, b) => num(a.sOrder) - num(b.sOrder))
          .map((c) => ({ id: num(c.surveysCustomOptionID ?? c.surveysOptionID) || null, text: str(c.optionText) })),
        options: null,
        groupId: num(groupItem?.surveysOrderGroupID),
        // The key the e-form processor reads; a legacy numeric id is shown as its key.
        populates: recordText || (recordId ? (demographicById.get(recordId) ?? String(recordId)) : ''),
        logicAction: logicEntry?.action ?? 0,
        logicCondition: logicEntry?.condition ?? 0,
        rules: logicEntry?.rules ?? [],
      };
    });

  return { fields, groups, optionGroups };
}

/** The options a choice question shows: its edits, else its group's, else its legacy set's. */
export function fieldOptions(field: BuilderField, optionGroups: OptionGroup[]): QuestionOption[] {
  if (field.options) return field.options;
  if (field.optionGroupId) {
    const group = optionGroups.find((g) => g.id === field.optionGroupId);
    if (group && group.options.length > 0) return group.options;
  }
  return field.customSetOptions;
}

/** Corp's blocking rules, per question. */
export function fieldErrors(field: BuilderField, optionGroups: OptionGroup[]): string[] {
  const errors: string[] = [];
  if (field.type !== PAGE_BREAK && field.type !== 15 && !htmlHasContent(field.content)) errors.push('Question content is required');
  if (CHOICE_TYPES.includes(field.type) && fieldOptions(field, optionGroups).every((o) => !o.text.trim())) {
    errors.push('At least one option is required');
  }
  if (field.type === CREDIT_CARD && !field.instruction.trim()) errors.push('Merchant ID is required');
  return errors;
}

/** One question as PUT /surveys/{id}/fields takes it. */
export function fieldPayload(field: BuilderField, index: number): Record<string, unknown> {
  const isAddress = field.type === ADDRESS;
  const custom = isAddress ? field.addressMask : CHOICE_TYPES.includes(field.type) ? field.optionGroupId : 0;
  const hasLogic = field.rules.some((r) => r.targetOrderId);
  return {
    id: field.orderId,
    content: field.type === PAGE_BREAK ? encodeEmoji(field.content.trim()) : encodeEmoji(field.content),
    instruction: field.type === CREDIT_CARD ? field.instruction : encodeEmoji(field.instruction),
    type: String(field.type),
    required: (isAddress ? field.addressMask > 0 : field.required) ? '1' : '0',
    other: field.addOther ? '1' : '0',
    checkAll: field.type === 8 && field.checkAll ? '1' : '0',
    summary: field.summary ? '1' : '0',
    options: `${custom}|${field.customSetId}`,
    questionGroup: String(field.groupId || 0),
    populates: field.populates,
    logicAction: String(hasLogic ? field.logicAction : 0),
    logicCondition: String(field.logicCondition),
    order: index + 1,
    showTo: String(field.showTo),
    logicRules: field.rules
      .filter((r) => r.targetOrderId)
      .map((r) => ({
        surveysItemLogicRuleID: r.id ?? '',
        targetItemID: r.targetOrderId,
        logicBoolean: String(r.operator),
        surveysOptionID: String(r.optionId || 0),
        responseContent: r.value,
      })),
  };
}

export class SharedOptionGroupError extends Error {
  constructor(public readonly groupTitle: string) {
    super(
      `"${groupTitle}" is used by more than one account, so editing it would change every form that shares it. ` +
        'Pick another group for this question (or create one), then edit that.',
    );
  }
}

export function createOptionGroup(title: string, options: QuestionOption[]): Promise<number> {
  return apiClient
    .post<ApiResponse<Record<string, unknown>>>('/survey-question-types', {
      title: encodeEmoji(title.trim()).slice(0, 255),
      options: options.filter((o) => o.text.trim()).map((o, i) => ({ text: encodeEmoji(o.text.trim()), order: i + 1 })),
    })
    .then((r) => num(r.data?.data?.surveysQuestionTypeID ?? r.data?.data?.id));
}

/** Replaces the group's options (options left out are deleted). */
export async function updateOptionGroup(id: number, title: string, options: QuestionOption[]): Promise<void> {
  try {
    await apiClient.put(`/survey-question-types/${id}`, {
      title: encodeEmoji(title.trim()).slice(0, 255),
      options: options.filter((o) => o.text.trim()).map((o, i) => ({ id: o.id, text: encodeEmoji(o.text.trim()), order: i + 1 })),
    });
  } catch (err) {
    const res = (err as { response?: { status?: number; data?: { error?: { code?: string } } } })?.response;
    if (res?.status === 403 && res.data?.error?.code === 'SHARED_GROUP') throw new SharedOptionGroupError(title);
    throw err;
  }
}

/**
 * Save All: first the edited options — into the question's group (all edits:
 * renamed, removed, reordered and new), or, for a question without one, into
 * a new group named after it — then every question in one call. A shared
 * group stops the save before any question is written.
 */
export async function saveBuilder(surveyId: number, fields: BuilderField[], optionGroups: OptionGroup[]): Promise<void> {
  const prepared: BuilderField[] = [];
  for (const [i, field] of fields.entries()) {
    if (!CHOICE_TYPES.includes(field.type) || !field.options) {
      prepared.push(field);
      continue;
    }
    // A legacy set's options carry ids from another table: written to a group they are new.
    const fromGroup = field.optionGroupId > 0;
    const options = fromGroup ? field.options : field.options.map((o) => ({ ...o, id: null }));
    if (fromGroup) {
      const group = optionGroups.find((g) => g.id === field.optionGroupId);
      await updateOptionGroup(field.optionGroupId, group?.title || `Option Set ${i + 1}`, options);
      prepared.push({ ...field, options: null });
    } else {
      const title = plainText(field.content).slice(0, 100) || `Option Set ${i + 1}`;
      const id = await createOptionGroup(title, options);
      prepared.push({ ...field, optionGroupId: id, options: null });
    }
  }
  await apiClient.put(`/surveys/${surveyId}/fields`, { fields: prepared.map(fieldPayload) });
}
